/**
 * NotificationContext - DeepThought Engine Notification System
 * 
 * Provides a centralized notification system for:
 * - Toast notifications (temporary, auto-dismiss)
 * - Persistent notifications (require action)
 * - Plugin notifications via PluginNotificationData
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, useMemo, type Dispatch } from 'react';
import { io, Socket } from 'socket.io-client';
import client, { API_BASE } from '../api/client';
import { useAuthStore } from '../stores/authStore';

/**
 * Generic plugin notification data.
 * Plugins can pass any data — the core system
 * uses only title, message, id, and priority.
 */
export interface PluginNotificationData {
  id: string;
  title: string;
  message?: string;
  priority?: string;
  suggestedActions?: Array<{ label: string; url?: string }>;
  [key: string]: any;
}

// ================================
// Types
// ================================

export type NotificationType = 'info' | 'success' | 'warning' | 'error' | (string & {});

export interface ToastNotification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number; // ms, 0 = persistent
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  sourceId?: string; // Link to originating plugin entity if applicable
  createdAt: Date;
}

export interface PersistentNotification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  read: boolean;
  /** Plugin-provided data */
  pluginData?: PluginNotificationData;
  createdAt: Date;
  actionUrl?: string;
  actionLabel?: string;
}

interface NotificationState {
  toasts: ToastNotification[];
  notifications: PersistentNotification[];
  unreadCount: number;
  isLoading: boolean;
  isCenterOpen: boolean;
}

type NotificationAction =
  | { type: 'ADD_TOAST'; payload: ToastNotification }
  | { type: 'REMOVE_TOAST'; payload: string }
  | { type: 'ADD_NOTIFICATION'; payload: PersistentNotification }
  | { type: 'MARK_READ'; payload: string }
  | { type: 'MARK_ALL_READ' }
  | { type: 'DISMISS_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_CENTER_OPEN'; payload: boolean }
  | { type: 'TOGGLE_CENTER' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_NOTIFICATIONS'; payload: PersistentNotification[] };

interface NotificationContextType {
  state: NotificationState;
  // Toast methods
  showToast: (toast: Omit<ToastNotification, 'id' | 'createdAt'>) => string;
  showSuccess: (title: string, message?: string) => string;
  showError: (title: string, message?: string) => string;
  showWarning: (title: string, message?: string) => string;
  showInfo: (title: string, message?: string) => string;
  showPluginToast: (data: PluginNotificationData) => string;
  dismissToast: (id: string) => void;
  // Persistent notification methods
  addNotification: (notification: Omit<PersistentNotification, 'id' | 'createdAt' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismissNotification: (id: string) => void;
  clearAll: () => void;
  // Center control
  openCenter: () => void;
  closeCenter: () => void;
  toggleCenter: () => void;
}

// ================================
// Reducer
// ================================

const initialState: NotificationState = {
  toasts: [],
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  isCenterOpen: false,
};

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, action.payload],
      };

    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter(t => t.id !== action.payload),
      };

    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + 1,
      };

    case 'MARK_READ': {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (!notification || notification.read) return state;
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    }

    case 'MARK_ALL_READ':
      return {
        ...state,
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0,
      };

    case 'DISMISS_NOTIFICATION': {
      const dismissedNotif = state.notifications.find(n => n.id === action.payload);
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
        unreadCount: dismissedNotif && !dismissedNotif.read 
          ? Math.max(0, state.unreadCount - 1) 
          : state.unreadCount,
      };
    }

    case 'CLEAR_ALL':
      return {
        ...state,
        notifications: [],
        unreadCount: 0,
      };

    case 'SET_CENTER_OPEN':
      return {
        ...state,
        isCenterOpen: action.payload,
      };

    case 'TOGGLE_CENTER':
      return {
        ...state,
        isCenterOpen: !state.isCenterOpen,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload,
        unreadCount: action.payload.filter(n => !n.read).length,
      };

    default:
      return state;
  }
}

// ================================
// Context
// ================================

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// ================================
// Helper functions
// ================================

let notificationIdCounter = 0;

function generateId(): string {
  return `notif-${Date.now()}-${++notificationIdCounter}`;
}

/** Duration defaults for core types; plugin types fall through to priority-based logic. */
const CORE_DURATION: Record<string, number> = {
  error: 8000,
  warning: 6000,
  success: 4000,
  info: 5000,
};

const PRIORITY_DURATION: Record<string, number> = {
  critical: 0,   // Persistent
  high: 10000,
  medium: 5000,
  low: 3000,
};

/**
 * A reminder going off while the tab is in the background also gets an OS
 * notification (the reminders page asks for the permission). The phone gets
 * its own push from the backend.
 */
function showBackgroundSystemNotification(data: any): void {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && !document.hidden) return;
    const notification = new Notification(data.title, {
      body: data.body ?? '',
      tag: data.metadata?.pushTag ?? data.id,
    });
    notification.onclick = () => {
      window.focus();
      if (data.actionUrl) window.location.href = data.actionUrl;
      notification.close();
    };
  } catch {
    // Some browsers only allow notifications from a service worker.
  }
}

function getDefaultDuration(type: NotificationType, priority?: string): number {
  // Core types have fixed durations
  if (CORE_DURATION[type] !== undefined) return CORE_DURATION[type];
  // Plugin types use priority-based durations
  if (priority && PRIORITY_DURATION[priority] !== undefined) return PRIORITY_DURATION[priority];
  return 5000;
}

// ================================
// Provider
// ================================

interface NotificationProviderProps {
  children: React.ReactNode;
  maxToasts?: number;
  maxNotifications?: number;
}

export function NotificationProvider({
  children,
  maxToasts = 5,
  maxNotifications = 100,
}: NotificationProviderProps) {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const toastTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Keep a ref to state for use in callbacks to avoid stale closures
  const stateRef = useRef(state);
  stateRef.current = state;

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      toastTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // Signed-in state. This provider wraps <App/> outside any auth gate, so the
  // backend sync below must wait for a token: a leftover userId alone (expired
  // session) used to fire an anonymous GET that 401'd, and nothing refetched
  // after a fresh login. The backend takes the user from the token.
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.userId);

  // ── Backend sync: fetch persisted notifications once signed in ──
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    client.get('/api/notifications', { params: { limit: 50 } })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.notifications?.length) {
          const mapped: PersistentNotification[] = data.notifications.map((n: any) => ({
            id: n.id,
            type: n.level || 'info',
            title: n.title,
            message: n.body,
            read: n.read,
            createdAt: new Date(n.createdAt),
            actionUrl: n.actionUrl,
            actionLabel: n.actionLabel,
            pluginData: n.metadata?.pluginData || (n.sourcePlugin ? { id: n.sourcePlugin, title: n.sourcePlugin } : undefined),
          }));
          dispatch({ type: 'SET_NOTIFICATIONS', payload: mapped });
        }
      })
      .catch(() => { /* silent — backend may not be running */ });

    return () => {
      cancelled = true;
      // Signed out or switched account: drop the previous user's list.
      dispatch({ type: 'SET_NOTIFICATIONS', payload: [] });
    };
  }, [token]);

  // ── Socket.IO: real-time notification delivery ──
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token || !userId) return;

    const socket = io(`${API_BASE}/notifications`, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      auth: { token },
    });

    socket.on('connect', () => {
      socket.emit('notifications:join', { userId });
    });

    socket.on('notification:new', (data: any) => {
      // Add to persistent list
      const persistent: PersistentNotification = {
        id: data.id,
        type: data.level || 'info',
        title: data.title,
        message: data.body,
        read: false,
        createdAt: new Date(data.createdAt),
        actionUrl: data.actionUrl,
        actionLabel: data.actionLabel,
        pluginData: data.metadata?.pluginData || (data.sourcePlugin ? { id: data.sourcePlugin, title: data.sourcePlugin } : undefined),
      };
      dispatch({ type: 'ADD_NOTIFICATION', payload: persistent });

      // Show toast popup — a due reminder stays up until dismissed.
      const isReminder = data.sourcePlugin === 'reminders';
      const duration = isReminder ? 0 : getDefaultDuration(data.level || 'info');
      if (isReminder) showBackgroundSystemNotification(data);
      const toastId = generateId();
      const newToast: ToastNotification = {
        id: toastId,
        type: data.level || 'info',
        title: data.title,
        message: data.body,
        duration,
        dismissible: true,
        sourceId: data.id,
        createdAt: new Date(),
        action: data.actionUrl ? {
          label: data.actionLabel || 'View',
          onClick: () => {
            window.location.href = data.actionUrl;
          },
        } : undefined,
      };
      dispatch({ type: 'ADD_TOAST', payload: newToast });

      // Schedule auto-dismiss for the toast
      if (duration > 0) {
        const timeout = setTimeout(() => {
          dispatch({ type: 'REMOVE_TOAST', payload: toastId });
          toastTimeoutsRef.current.delete(toastId);
        }, duration);
        toastTimeoutsRef.current.set(toastId, timeout);
      }
    });

    socketRef.current = socket;
    return () => { socket.close(); socketRef.current = null; };
  }, [token, userId]);

  // Auto-dismiss toasts
  const scheduleToastDismissal = useCallback((id: string, duration: number) => {
    if (duration <= 0) return; // Persistent toast

    const timeout = setTimeout(() => {
      dispatch({ type: 'REMOVE_TOAST', payload: id });
      toastTimeoutsRef.current.delete(id);
    }, duration);

    toastTimeoutsRef.current.set(id, timeout);
  }, []);

  const showToast = useCallback((toast: Omit<ToastNotification, 'id' | 'createdAt'>): string => {
    const id = generateId();
    const duration = toast.duration ?? getDefaultDuration(toast.type);
    const newToast: ToastNotification = {
      ...toast,
      id,
      createdAt: new Date(),
      duration,
      dismissible: toast.dismissible ?? true,
    };

    // Limit toasts — read current count via ref to avoid stale closure
    if (stateRef.current.toasts.length >= maxToasts) {
      const oldestToast = stateRef.current.toasts[0];
      dispatch({ type: 'REMOVE_TOAST', payload: oldestToast.id });
      const oldTimeout = toastTimeoutsRef.current.get(oldestToast.id);
      if (oldTimeout) {
        clearTimeout(oldTimeout);
        toastTimeoutsRef.current.delete(oldestToast.id);
      }
    }

    dispatch({ type: 'ADD_TOAST', payload: newToast });
    scheduleToastDismissal(id, duration);

    return id;
  }, [maxToasts, scheduleToastDismissal]);

  const dismissToast = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', payload: id });
    const timeout = toastTimeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      toastTimeoutsRef.current.delete(id);
    }
  }, []);

  const showSuccess = useCallback((title: string, message?: string): string => {
    return showToast({ type: 'success', title, message });
  }, [showToast]);

  const showError = useCallback((title: string, message?: string): string => {
    return showToast({ type: 'error', title, message });
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string): string => {
    return showToast({ type: 'warning', title, message });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string): string => {
    return showToast({ type: 'info', title, message });
  }, [showToast]);

  const showPluginToast = useCallback((data: PluginNotificationData): string => {
    const duration = getDefaultDuration('plugin', data.priority);
    const primaryAction = data.suggestedActions?.[0];
    return showToast({
      type: 'plugin',
      title: data.title,
      message: data.message,
      duration,
      sourceId: data.id,
      action: primaryAction ? {
        label: primaryAction.label,
        onClick: () => {
          if (primaryAction.url) {
            window.location.href = primaryAction.url;
          }
        },
      } : undefined,
    });
  }, [showToast]);

  const addNotification = useCallback((notification: Omit<PersistentNotification, 'id' | 'createdAt' | 'read'>) => {
    const newNotification: PersistentNotification = {
      ...notification,
      id: generateId(),
      createdAt: new Date(),
      read: false,
    };

    // Limit notifications — read current count via ref to avoid stale closure
    if (stateRef.current.notifications.length >= maxNotifications) {
      const oldestNotification = stateRef.current.notifications[stateRef.current.notifications.length - 1];
      dispatch({ type: 'DISMISS_NOTIFICATION', payload: oldestNotification.id });
    }

    dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });
  }, [maxNotifications]);

  // Backend writes go through the axios client (Bearer token) and only while
  // signed in; the backend scopes them to the token's user.
  const markAsRead = useCallback((id: string) => {
    dispatch({ type: 'MARK_READ', payload: id });
    if (!useAuthStore.getState().token) return;
    client.patch(`/api/notifications/${encodeURIComponent(id)}/read`).catch(() => {});
  }, []);

  const markAllAsRead = useCallback(() => {
    dispatch({ type: 'MARK_ALL_READ' });
    if (!useAuthStore.getState().token) return;
    client.post('/api/notifications/read-all').catch(() => {});
  }, []);

  const dismissNotification = useCallback((id: string) => {
    dispatch({ type: 'DISMISS_NOTIFICATION', payload: id });
    if (!useAuthStore.getState().token) return;
    client.delete(`/api/notifications/${encodeURIComponent(id)}`).catch(() => {});
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
    if (!useAuthStore.getState().token) return;
    client.delete('/api/notifications').catch(() => {});
  }, []);

  const openCenter = useCallback(() => {
    dispatch({ type: 'SET_CENTER_OPEN', payload: true });
  }, []);

  const closeCenter = useCallback(() => {
    dispatch({ type: 'SET_CENTER_OPEN', payload: false });
  }, []);

  const toggleCenter = useCallback(() => {
    dispatch({ type: 'TOGGLE_CENTER' });
  }, []);

  const value: NotificationContextType = useMemo(() => ({
    state,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showPluginToast,
    dismissToast,
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAll,
    openCenter,
    closeCenter,
    toggleCenter,
  }), [
    state,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showPluginToast,
    dismissToast,
    addNotification,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    clearAll,
    openCenter,
    closeCenter,
    toggleCenter,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// ================================
// Hook
// ================================

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationProvider;
